import { Fragment, useMemo } from 'react';
import classNames from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { Autocomplete, Button } from '@crazyegginc/hatch';

import { useSelectedSite } from '/src/hooks';
import { AUDIENCE_METADATA } from '../../../metadata';
import { generateEmptyConditionRow } from '../../../utils';
import { ConditionTooltip } from '../ConditionTooltip';

import { ReactComponent as CrossIcon } from '@crazyegginc/hatch/dist/images/icon-cross.svg';

export function AsyncSelectValue({ index, innerIndex, mode, rowActions, valueActions, extra }) {
  const { values, errors, touched, setFieldValue } = useFormikContext();
  const { selectedSite } = useSelectedSite();
  const condition = values.conditions[index];
  const { criteria, rows } = condition;
  const row = rows[innerIndex];
  const rowValues = Array.isArray(row.values) ? row.values : [''];
  const isLastRow = innerIndex === rows.length - 1;
  const valueTooltips = row.valueTooltips ?? [];

  const metadata = AUDIENCE_METADATA[criteria];
  const { asyncOptions, multiOr, multiAnd, placeholder } = metadata;
  const queryContext = { selectedSite };
  const enabled = asyncOptions.enabled?.(queryContext) ?? true;
  const { data, isFetching, isError } = useQuery({
    ...asyncOptions.query(queryContext),
    enabled,
  });
  const options = useAsyncSelectOptions({
    data,
    asyncOptions,
    mode,
    rowValues,
    isError,
  });

  const rowTouched = touched.conditions?.[index]?.rows?.[innerIndex]?.values;
  const rowError = errors.conditions?.[index]?.rows?.[innerIndex]?.values;

  function onTooltipDismiss(valueIndex) {
    setFieldValue(
      `conditions[${index}].rows[${innerIndex}].valueTooltips`,
      valueTooltips.filter((tooltipIndex) => tooltipIndex !== valueIndex),
      false,
    );
  }

  return (
    <div className="flex flex-wrap gap-y-2.5">
      {rowValues.map((value, i) => {
        const valueInput = (
          <div className="relative w-[250px]">
            <Autocomplete
              value={value}
              options={getAvailableOptions({ options, rowValues, index: i })}
              placeholder={placeholder}
              onChange={(newValue) => setFieldValue(`conditions[${index}].rows[${innerIndex}].values[${i}]`, newValue)}
              aria-label={criteria}
              error={getValueError({ rowTouched, rowError, index: i })}
              loading={isFetching || !enabled}
              allowCustom={false}
              renderInPortal={true}
              popperClassName="!z-[100000]"
              inputClassName="!pr-12 !pl-2.5"
            />
            {rowValues.length > 1 || rows.length > 1 ? (
              <button
                type="button"
                onClick={() => {
                  if (rowValues.length > 1) {
                    valueActions.remove(i);
                  } else {
                    rowActions.remove(innerIndex);
                  }
                }}
                className={classNames(
                  'absolute right-7 top-0 flex h-[35px] w-[20px] items-center justify-center ',
                  'text-cadet-blue-500 hover:text-radical-red-500 focus:text-radical-red-500 focus-visible:outline-black',
                )}
              >
                <CrossIcon className="h-2.5 w-2.5 fill-current" aria-label="delete condition value" />
              </button>
            ) : null}
          </div>
        );

        return (
          <Fragment key={i}>
            {multiOr && i > 0 ? (
              <div className="text-body-5 font-semibold ml-2.5 flex h-[35px] w-[47px] items-center justify-center">
                or
              </div>
            ) : null}
            <ConditionTooltip
              criteria={metadata}
              show={valueTooltips.includes(i)}
              onDismiss={() => onTooltipDismiss(i)}
            >
              {valueInput}
            </ConditionTooltip>
          </Fragment>
        );
      })}
      {multiOr ? (
        <Button variant="secondary" onClick={() => valueActions.push('')} className="!px-2.5 ml-2.5">
          or...
        </Button>
      ) : null}
      {multiAnd ? (
        <Button
          variant="secondary"
          onClick={() => rowActions.insert(innerIndex + 1, generateEmptyConditionRow(criteria))}
          className="!px-2.5 ml-2.5"
        >
          and...
        </Button>
      ) : null}
      {extra && isLastRow ? extra : null}
    </div>
  );
}

function useAsyncSelectOptions({ data, asyncOptions, mode, rowValues, isError }) {
  return useMemo(() => {
    const loadedOptions = asyncOptions.getOptions({ data, mode });

    if (loadedOptions.length > 0) {
      return withSelectedFallbackOptions({ options: loadedOptions, rowValues });
    }

    if (isError) {
      return withSelectedFallbackOptions({
        options: [{ value: '', label: 'Unable to load options', disabled: true }],
        rowValues,
      });
    }

    return withSelectedFallbackOptions({ options: [], rowValues });
  }, [asyncOptions, data, isError, mode, rowValues]);
}

function withSelectedFallbackOptions({ options, rowValues }) {
  const optionValues = new Set(options.map((option) => option.value));
  const fallbackOptions = rowValues
    .filter((value) => value && !optionValues.has(value))
    .map((value) => ({ value, label: value }));

  return [...options, ...fallbackOptions];
}

function getAvailableOptions({ options, rowValues, index }) {
  const selectedValues = new Set(rowValues.filter((value, valueIndex) => value && valueIndex !== index));

  return options.filter(
    (option) => option.disabled || option.value === rowValues[index] || !selectedValues.has(option.value),
  );
}

function getValueError({ rowTouched, rowError, index }) {
  if (!rowTouched || !rowError) return null;

  if (typeof rowError === 'string') {
    return index === 0 ? rowError : null;
  }

  return rowTouched[index] ? (rowError[index] ?? null) : null;
}
